import { motion } from "framer-motion";
import {
  HiArrowRight,
  HiBriefcase,
  HiCheckCircle,
  HiLocationMarker,
  HiSparkles,
} from "react-icons/hi";
import SectionTitle from "../components/SectionTitle";
import { careerBenefits, careerOpenings } from "../data/content";
import { fadeInUp, stagger } from "../utils/motion";

function CareerSection() {
  return (
    <section id="career" className="relative py-24 sm:py-28">
      <div className="section-shell">
        <SectionTitle
          eyebrow="Career"
          title="Build Meaningful Digital Products With Us"
          subtitle="Join BeeVerge to work on real product challenges, modern technology, and a culture that values ownership, learning, and clean execution."
        />

        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            className="glass overflow-hidden rounded-2xl shadow-glass"
          >
            <div className="relative h-80 overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=900&q=80"
                alt="BeeVerge team career culture"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05050b] via-[#05050b]/35 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="inline-flex items-center gap-2 rounded-full border border-cyan-200/25 bg-black/45 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-100 backdrop-blur">
                  <HiSparkles className="h-4 w-4" />
                  Hiring Creative Builders
                </p>
                <h3 className="mt-4 text-2xl font-bold text-white sm:text-3xl">Grow With A Product Team</h3>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <h4 className="text-xl font-semibold text-white">Why Join BeeVerge?</h4>
              <div className="mt-5 space-y-3">
                {careerBenefits.map((benefit) => (
                  <div key={benefit} className="flex gap-3 text-sm leading-6 text-blue-100/82">
                    <HiCheckCircle className="mt-1 h-5 w-5 shrink-0 text-cyan-300" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            className="space-y-5"
          >
            {careerOpenings.map((job) => (
              <motion.article
                key={job.title}
                variants={fadeInUp}
                whileHover={{ x: 6 }}
                className="glass rounded-2xl p-6 shadow-glass"
              >
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div>
                    <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-300 sm:tracking-[0.16em]">
                      <HiBriefcase className="h-4 w-4" />
                      {job.type}
                    </p>
                    <h3 className="mt-3 text-xl font-bold text-white sm:text-2xl">{job.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-blue-100/76">{job.summary}</p>
                  </div>
                  <a
                    href="#contact"
                    className="inline-flex w-fit shrink-0 items-center gap-2 rounded-lg border border-cyan-200/25 bg-cyan-300/10 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-cyan-100 transition hover:shadow-neon sm:tracking-[0.14em]"
                  >
                    Apply
                    <HiArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-xs text-blue-100/78">
                    <HiLocationMarker className="h-4 w-4 text-cyan-300" />
                    {job.location}
                  </span>
                  <span className="rounded-lg border border-white/10 bg-black/25 px-3 py-2 text-xs text-blue-100/78">
                    {job.experience}
                  </span>
                  {job.skills.map((skill) => (
                    <span
                      key={skill}
                      className="rounded-lg border border-cyan-200/15 bg-cyan-300/10 px-3 py-2 text-xs font-semibold text-cyan-100"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default CareerSection;
